import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { countShort, num } from "../../lib/format";
import type { Projection } from "../../lib/demographics";

export type PopulationView = "people" | "households";

/**
 * Fifty-five years of censuses on the left, a scenario on the right.
 *
 * Drawn like the price projection deliberately — solid for recorded, dashed
 * inside a band for imagined — because they are the same kind of object and
 * the reader should not have to learn a second convention. The band here is
 * INSEE's own low-to-high population scenarios, which is a wider and more
 * honest spread than anything we could invent.
 */
export function PopulationChart({
  projection,
  view,
}: {
  projection: Projection;
  view: PopulationView;
}) {
  const people = view === "people";
  const rows = projection.rows.map((r) => {
    // Households have no published band of their own: they are the population
    // band divided by one household-size path, so the band is scaled in the
    // same proportion rather than implying a precision that is not there.
    const scale =
      people || !r.projected || !r.projectedHouseholds
        ? 1
        : r.projectedHouseholds / r.projected;
    return {
      year: r.year,
      actual: people ? r.pop : r.households,
      projected: people ? r.projected : r.projectedHouseholds,
      band: r.band
        ? ([r.band[0] * scale, r.band[1] * scale] as [number, number])
        : null,
    };
  });

  const known = rows
    .flatMap((r) => [r.actual, r.projected, ...(r.band ?? [])])
    .filter((v): v is number => v !== null && Number.isFinite(v));
  const lo = Math.min(...known);
  const hi = Math.max(...known);
  const pad = Math.max((hi - lo) * 0.08, hi * 0.02);

  return (
    <div className="h-40 w-full">
      <ResponsiveContainer>
        <ComposedChart data={rows} margin={{ top: 6, right: 6, bottom: 0, left: -6 }}>
          <XAxis
            dataKey="year"
            type="number"
            domain={["dataMin", "dataMax"]}
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            ticks={tickYears(rows[0]?.year, rows[rows.length - 1]?.year)}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            width={40}
            domain={[Math.floor(lo - pad), Math.ceil(hi + pad)]}
            tickFormatter={countShort}
          />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e3e7ed" }}
            formatter={(value, name, item) => {
              const r = item.payload as (typeof rows)[number];
              if (name === "actual") return [num(Math.round(r.actual ?? 0)), "census"];
              if (name === "projected")
                return [num(Math.round(r.projected ?? 0)), "scenario"];
              if (name === "band" && r.band)
                return [
                  `${countShort(r.band[0])} – ${countShort(r.band[1])}`,
                  "official range",
                ];
              return [String(value), String(name)];
            }}
          />
          <Area
            dataKey="band"
            stroke="none"
            fill="#2f6df6"
            fillOpacity={0.12}
            isAnimationActive={false}
            activeDot={false}
          />
          <ReferenceLine
            x={projection.base.year}
            stroke="#5a6472"
            strokeDasharray="2 3"
            strokeOpacity={0.5}
          />
          <Line
            dataKey="actual"
            stroke="#14181f"
            strokeWidth={2}
            dot={{ r: 1.8 }}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Line
            dataKey="projected"
            stroke="#2f6df6"
            strokeWidth={2}
            strokeDasharray="4 3"
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Ticks every twenty years plus both ends.
 *
 * The censuses are unevenly spaced — seven years, then eight, then nine — so
 * letting recharts label the data points gives a crowded, irregular axis.
 */
function tickYears(first?: number, last?: number): number[] {
  if (first === undefined || last === undefined) return [];
  const ticks = [first];
  for (let y = Math.ceil(first / 20) * 20; y < last; y += 20) ticks.push(y);
  ticks.push(last);
  return ticks;
}
